import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, AlertCircle, CheckCircle2 } from "lucide-react";
import Layout from "../components/Layout";
import PasswordInput from "../components/PasswordInput";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { rawSignUp } from "../lib/gotrueAuth";

export default function Signup() {
  const { user, isSupabaseConfigured } = useCustomerAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/os-meus-casos");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("A password deve ter pelo menos 8 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const result = await rawSignUp(email.toLowerCase(), password);
      if (result.ok) {
        if (result.session) {
          nav("/os-meus-casos");
          return;
        }
        // Email confirmation required
        setSuccess(true);
        return;
      }
      if (result.exists) {
        setError("Já existe uma conta com este email. Faz login.");
        return;
      }
      setError(result.error || "Não foi possível criar conta.");
    } catch (err) {
      setError(err.message || "Não foi possível criar conta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="min-h-[70vh] flex items-center py-20">
        <div className="max-w-md mx-auto px-6 w-full">
          <div className="dossier p-10">
            <div className="flex items-center gap-3 mb-2">
              <UserPlus className="w-4 h-4 text-blood" />
              <p className="mono text-[10px] tracking-[0.3em] text-blood">REGISTO DE INVESTIGADOR</p>
            </div>
            <h1 className="headline text-3xl mb-2">Criar conta</h1>
            <p className="text-bone-muted text-sm mb-8">
              Cria a tua conta para guardar os teus dossiers comprados num só sítio.
            </p>

            {!isSupabaseConfigured && (
              <div className="border border-blood/40 bg-blood/10 p-4 mb-6 flex gap-3">
                <AlertCircle className="w-4 h-4 text-blood shrink-0 mt-0.5" />
                <p className="mono text-[11px] text-bone-dim leading-relaxed">
                  Autenticação ainda não configurada. Configura SUPABASE_URL e SUPABASE_ANON_KEY no
                  ficheiro .env do frontend.
                </p>
              </div>
            )}

            {success ? (
              <div className="text-center py-6">
                <CheckCircle2 className="w-14 h-14 text-blood mx-auto mb-5" />
                <p className="mono text-[11px] tracking-[0.3em] text-blood mb-3">CONTA CRIADA</p>
                <h3 className="headline text-2xl mb-3">Verifica o teu email</h3>
                <p className="text-bone-muted text-sm leading-relaxed mb-6">
                  Enviámos uma mensagem para <strong className="text-white">{email}</strong>.
                  Confirma o teu endereço e depois entra.
                </p>
                <Link to="/entrar" data-testid="signup-go-login">
                  <button className="btn-ghost mx-auto">Ir para o login</button>
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <Field label="Email">
                  <input
                    data-testid="signup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="cec-input"
                    autoComplete="email"
                  />
                </Field>
                <Field label="Password (mínimo 8 caracteres)">
                  <PasswordInput
                    testId="signup-password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    minLength={8}
                  />
                </Field>
                {error && (
                  <p className="mono text-xs text-blood tracking-[0.15em]">{String(error)}</p>
                )}
                <button
                  type="submit"
                  disabled={busy || !isSupabaseConfigured}
                  data-testid="signup-submit"
                  className="btn-primary w-full justify-center disabled:opacity-50"
                >
                  {busy ? "A criar conta..." : (<>Criar conta <UserPlus className="w-4 h-4" /></>)}
                </button>
              </form>
            )}

            {!success && (
              <p className="mono text-[11px] text-bone-muted mt-8 text-center">
                Já tens conta?{" "}
                <Link to="/entrar" className="text-blood hover:underline">
                  Entrar
                </Link>
              </p>
            )}
          </div>
        </div>
      </section>
      <style>{`.cec-input{width:100%;background:#111;border:1px solid rgba(255,255,255,0.1);padding:12px 14px;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#fff;letter-spacing:0.05em}.cec-input:focus{outline:none;border-color:#C8102E}`}</style>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">{label.toUpperCase()}</label>
    {children}
  </div>
);
